import { useState } from 'react';
import { api, OrgProject } from '../../api';
import { getErrorMessage } from '../../../../shared/src';

interface CreateProjectModalProps {
  orgId: string;
  onClose: () => void;
  onSuccess: (project: OrgProject) => void;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function CreateProjectModal({ orgId, onClose, onSuccess }: CreateProjectModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugManuallyEdited) {
      setSlug(generateSlug(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const project = await api.createOrgProject(orgId, name.trim(), slug.trim());
      onSuccess(project);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Create Project</h2>
        <p className="modal-description">
          Create a new project. The slug is used in URLs and must be unique within the organization.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="project-name">Project Name</label>
            <input
              type="text"
              id="project-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              autoFocus
              placeholder="e.g. My Project"
            />
          </div>
          <div className="form-group">
            <label htmlFor="project-slug">Slug</label>
            <input
              type="text"
              id="project-slug"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              required
              placeholder="e.g. my-project"
            />
            <small>Auto-generated from name. You can edit it manually.</small>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading || !name.trim() || !slug.trim()}>
              {loading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
